import { useUpdateEffect } from "@react-hookz/web";
import { Command as CommandPrimitive } from "cmdk";
import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandLoading,
} from "~/components/ui/command";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "~/components/ui/popover";
import { usePlacesDetailsQuery } from "~/hooks/use-places-details-query";
import { usePlacesSuggestionsQuery } from "~/hooks/use-places-suggestions-query";

const PlaceItem: React.FC<{
  place: google.maps.places.AutocompleteSuggestion;
}> = ({ place }) => {
  return (
    <div className="flex flex-1 items-center gap-2">
      <span className="text-sm">{place.placePrediction?.text.text}</span>
    </div>
  );
};

export function AddressAutocomplete({
  invalid,
  onChange,
  onValueChange,
  value,
  includedRegionCodes,
}: {
  invalid?: boolean;
  onChange: (value: string | undefined) => void;
  onValueChange: (value: google.maps.places.Place) => void;
  value: string | undefined;
  includedRegionCodes?: string[];
}) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);

  const [selectedValue, setSelectedValue] =
    useState<google.maps.places.AutocompleteSuggestion | null>(null);

  const { data: placesSuggestions, isLoading: isPlacesSuggestionsLoading } =
    usePlacesSuggestionsQuery({
      input: value ?? "",
      language: i18n.language,
      includedRegionCodes: includedRegionCodes,
    });
  const { data: placesDetails } = usePlacesDetailsQuery(
    {
      placeId: selectedValue?.placePrediction?.placeId ?? "",
      requestedLanguage: i18n.language,
      fieldsRequest: {
        fields: ["addressComponents"],
      },
    },
    {
      enabled: !!selectedValue?.placePrediction?.placeId,
    },
  );

  const onSelectItem = (
    placePrediction: google.maps.places.AutocompleteSuggestion,
  ) => {
    if (
      placePrediction.placePrediction?.placeId !==
      selectedValue?.placePrediction?.placeId
    ) {
      onChange(placePrediction.placePrediction?.text.text ?? "");
      setSelectedValue(placePrediction);
    }
    setOpen(false);
  };

  useUpdateEffect(() => {
    if (placesDetails) {
      onValueChange(placesDetails.place);
    }
  }, [placesDetails]);

  return (
    <div className="flex items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <Command shouldFilter={false} className="rounded">
          <PopoverAnchor asChild>
            <CommandPrimitive.Input
              asChild
              value={value}
              onValueChange={onChange}
              onKeyDown={(e) => setOpen(e.key !== "Escape")}
              onMouseDown={() => setOpen((open) => !!value || !open)}
              onFocus={() => setOpen(true)}
              aria-invalid={invalid}
            >
              <Input placeholder={t("address_autocomplete.placeholder")} />
            </CommandPrimitive.Input>
          </PopoverAnchor>
          {!open && <CommandList aria-hidden="true" className="hidden" />}
          <PopoverContent
            asChild
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (
                e.target instanceof Element &&
                e.target.hasAttribute("cmdk-input")
              ) {
                e.preventDefault();
              }
            }}
            className="w-[--radix-popover-trigger-width] p-0"
          >
            {value ? (
              <CommandList>
                {isPlacesSuggestionsLoading && (
                  <CommandLoading>
                    {t("address_autocomplete.loading")}
                  </CommandLoading>
                )}
                {!isPlacesSuggestionsLoading ? (
                  <CommandEmpty>
                    {t("address_autocomplete.no_result")}
                  </CommandEmpty>
                ) : null}

                <CommandGroup>
                  {placesSuggestions?.map((option) => (
                    <CommandItem
                      key={option.placePrediction?.placeId}
                      value={option.placePrediction?.placeId}
                      onMouseDown={(e) => e.preventDefault()}
                      onSelect={() => onSelectItem(option)}
                    >
                      <PlaceItem place={option} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            ) : null}
          </PopoverContent>
        </Command>
      </Popover>
    </div>
  );
}
